import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Icon,
  Text,
} from '@chakra-ui/react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { fetchCandidateList, deleteCandidate } from '../../api/recruitment/recruitment'
import { PlusIcon, Trash04Icon } from '../../assets/icons'
import { CANDIDATE_STATUS_COLORS, CANDIDATE_STATUS_LABELS } from '../../types/recruitment/recruitmentTypes'
import { useFormMutation } from '../../hooks/useFormMutation'
import AvatarCell from './base/AvatarCell'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import AddCandidateModal from '../modals/Recruitment/AddCandidateModal'
import { prefetchCandidateRowIntent } from '../../lib/performance/prefetch'

const CandidatesTable: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const itemsPerPage = 15
  const filtersKey = JSON.stringify(filters)

  const candidates = useQuery({
    queryKey: ['candidates', page, searchTerm, filtersKey],
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: () =>
      fetchCandidateList({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        filters,
      }),
  })

  React.useEffect(() => {
    const currentCount = candidates.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    queryClient.prefetchQuery({
      queryKey: ['candidates', nextPage, searchTerm, filtersKey],
      staleTime: 60 * 1000,
      queryFn: () =>
        fetchCandidateList({
          search: searchTerm,
          page: nextPage,
          pageSize: itemsPerPage,
          filters,
        }),
    })
  }, [candidates.data, page, searchTerm, filters, filtersKey, itemsPerPage, queryClient])

  const deleteMutation = useFormMutation({
    mutationFn: deleteCandidate,
    queryKey: 'candidates',
    onSuccess: () => setSelectedRows([]),
  })

  const tableData = candidates?.data?.data || []

  const statusFilterOptions = [
    { id: 'new', name: t('recruitment.statuses.new') },
    { id: 'screening', name: t('recruitment.statuses.screening') },
    { id: 'interview', name: t('recruitment.statuses.interview') },
    { id: 'offer', name: t('recruitment.statuses.offer') },
    { id: 'hired', name: t('recruitment.statuses.hired') },
    { id: 'rejected', name: t('recruitment.statuses.rejected') },
  ]

  const sourceFilterOptions = [
    { id: 'linkedin', name: t('recruitment.sources.linkedin') },
    { id: 'referral', name: t('recruitment.sources.referral') },
    { id: 'website', name: t('recruitment.sources.website') },
    { id: 'job_board', name: t('recruitment.sources.job_board') },
    { id: 'agency', name: t('recruitment.sources.agency') },
    { id: 'other', name: t('recruitment.sources.other') },
  ]

  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const columns: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('common.columns.candidate'),
      accessor: 'name',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.profile_picture} name={`${row.first_name} ${row.last_name}`} />
      ),
    },
    {
      label: t('common.columns.vacancy'),
      accessor: 'vacancy',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {row.vacancy?.title || '-'}
        </Text>
      ),
    },
    {
      label: t('common.fields.status'),
      accessor: 'status',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const colors = CANDIDATE_STATUS_COLORS[row.status as keyof typeof CANDIDATE_STATUS_COLORS] || { bg: '#E5E7EB', text: '#374151' }
        return (
          <Badge
            bg={colors.bg}
            color={colors.text}
            px="10px"
            py="4px"
            borderRadius="8px"
            fontSize="11px"
            fontWeight="600"
            textTransform="capitalize"
            border="1px solid"
            borderColor={colors.text}
            display="inline-flex"
            w="85px"
            alignItems="center"
            justifyContent="center"
          >
            {t(`recruitment.statuses.${row.status}`)}
          </Badge>
        )
      },
    },
    {
      label: t('common.columns.source'),
      accessor: 'source',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated textTransform="capitalize">
          {row.source ? t(`recruitment.sources.${row.source}`) : '-'}
        </Text>
      ),
    },
    {
      label: t('common.fields.email'),
      accessor: 'email',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {row.email || '-'}
        </Text>
      ),
    },
    {
      label: t('common.columns.applied'),
      accessor: 'applied_at',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" color="solid.400">
          {row.applied_at ? new Date(row.applied_at).toLocaleDateString() : '-'}
        </Text>
      ),
    },
  ]

  const handleSearch = (term: string) => {
    setPage(1)
    setSearchTerm(term)
  }

  const handleRowClick = (row: RowData) => {
    navigate(`/user/recruitment/candidates/${row.id}`)
  }

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const strId = String(id)
    if (isChecked) {
      setSelectedRows((prev) => [...prev, strId])
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== strId))
    }
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows(tableData.map((row: RowData) => String(row.id)))
    } else {
      setSelectedRows([])
    }
  }

  const handleDelete = async () => {
    try {
      await Promise.all(selectedRows.map((id) => deleteMutation.mutateAsync(id)))
      setSelectedRows([])
    } catch {
      // partial failure — list will refetch via onSuccess of individual mutations
    }
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} minW={0} px="20px">
          <SearchInput onSearch={handleSearch} />
          <Flex gap="8px" flexShrink={0}>
            {selectedRows.length > 0 ? (
              <Button
                onClick={handleDelete}
                variant="error"
                size="sm"
                h="32px"
                px="14px"
                fontSize="13px"
                fontWeight="600"
                borderRadius="8px"
              >
                <Icon fill="none" color="error.100" mr={2}>
                  <Trash04Icon />
                </Icon>
                {t('planning.actions.delete')} ({selectedRows.length})
              </Button>
            ) : (
              <>
                <FilterButton
                  label={t('common.fields.status')}
                  options={statusFilterOptions}
                  onApply={(selected) => updateFilter('status', selected)}
                />
                <FilterButton
                  label={t('common.columns.source')}
                  options={sourceFilterOptions}
                  onApply={(selected) => updateFilter('source', selected)}
                />
                <Button
                  variant="mono"
                  onClick={() => setIsAddModalOpen(true)}
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                >
                  <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white">
                    <PlusIcon />
                  </Icon>
                  {t('recruitment.actions.addCandidate')}
                </Button>
              </>
            )}
          </Flex>
        </Flex>
        {candidates.isLoading ? (
          <LoadingTable columns={columns} />
        ) : (
          <Box minHeight="58vh" overflowX="auto" overflowY="visible">
            <MasterTable
              columns={columns}
              data={tableData}
              onRowClick={handleRowClick}
              onRowIntent={(row) => prefetchCandidateRowIntent(queryClient, row.id)}

              selectedRows={selectedRows}
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
              allSelected={tableData.length > 0 && selectedRows.length === tableData.length}
              isIndeterminate={tableData.length > 0 && selectedRows.length > 0 && selectedRows.length < tableData.length}
              tableType="people"
            />
          </Box>
        )}
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          maxPage={candidates?.data?.max_page || 0}
          totalItems={candidates?.data?.count || 0}
          handlePreviousPage={() => setPage(page - 1)}
          handleNextPage={() => setPage(page + 1)}
          handlePageClick={(p: number) => setPage(p)}
        />
      </Grid>
      <AddCandidateModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </Card>
  )
}

export default CandidatesTable
